import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { AccountUpdateData, UserData } from '../types/myaccount';
import api from '../services/api';
import MyAccountCard from './MyAccountCard';
import MyAccountPageShell from './MyAccountPageShell';

interface MyAccountDetailsProps {
    wekaPagePermissions?: Record<string, boolean>;
}

interface AccountDetailsFormData {
    first_name: string;
    last_name: string;
    display_name: string;
    email: string;
    current_password: string;
    new_password: string;
    confirm_password: string;
}

const FORM_FIELDS: Array<keyof AccountDetailsFormData> = [
    'first_name',
    'last_name',
    'display_name',
    'email',
    'current_password',
    'new_password',
    'confirm_password',
];

const createEmptyFormData = (): AccountDetailsFormData => ({
    first_name: '',
    last_name: '',
    display_name: '',
    email: '',
    current_password: '',
    new_password: '',
    confirm_password: '',
});

const buildFormData = (user?: UserData | null): AccountDetailsFormData => ({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    display_name: user?.display_name || '',
    email: user?.email || '',
    current_password: '',
    new_password: '',
    confirm_password: '',
});

const hasFormChanges = (
    currentValues: AccountDetailsFormData,
    initialValues: AccountDetailsFormData
): boolean => FORM_FIELDS.some((field) => currentValues[field] !== initialValues[field]);

const hasPasswordInput = (formData: AccountDetailsFormData): boolean =>
    Boolean(formData.current_password || formData.new_password || formData.confirm_password);

const MyAccountDetails: React.FC<MyAccountDetailsProps> = ({ wekaPagePermissions = {} }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState<AccountDetailsFormData>(createEmptyFormData);
    const [initialFormData, setInitialFormData] = useState<AccountDetailsFormData>(createEmptyFormData);
    const canEditAccountDetails = wekaPagePermissions.edit_account_details !== false;
    const isDirty = hasFormChanges(formData, initialFormData);
    const passwordChangeActive = hasPasswordInput(formData);

    useEffect(() => {
        void loadUserData();
    }, []);

    const loadUserData = async () => {
        try {
            setLoading(true);
            const data = await api.getCurrentUser();
            const nextFormData = buildFormData(data);
            setFormData(nextFormData);
            setInitialFormData(nextFormData);
        } catch (err) {
            console.error('Failed to load user data:', err);
            toast.error('Failed to load account details');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        const fieldName = name as keyof AccountDetailsFormData;

        setFormData((previous) => ({
            ...previous,
            [fieldName]: value,
        }));
    };

    const handleDiscard = () => {
        if (saving) {
            return;
        }

        setFormData(initialFormData);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!canEditAccountDetails) {
            toast.error('Editing account details is disabled for this page.');
            return;
        }

        if (passwordChangeActive) {
            if (!formData.current_password) {
                toast.error('Please enter your current password to change your password.');
                return;
            }

            if (!formData.new_password) {
                toast.error('Please enter a new password.');
                return;
            }

            if (!formData.confirm_password) {
                toast.error('Please confirm your new password.');
                return;
            }

            if (formData.new_password !== formData.confirm_password) {
                toast.error('New passwords do not match.');
                return;
            }

            if (formData.new_password.length < 8) {
                toast.error('Password must be at least 8 characters long.');
                return;
            }
        }

        try {
            setSaving(true);

            const updateData: AccountUpdateData = {
                first_name: formData.first_name,
                last_name: formData.last_name,
                display_name: formData.display_name,
                email: formData.email,
            };

            if (passwordChangeActive) {
                updateData.current_password = formData.current_password;
                updateData.new_password = formData.new_password;
            }

            const updatedUser = await api.updateCurrentUser(updateData);
            const nextFormData = buildFormData(updatedUser);

            setFormData(nextFormData);
            setInitialFormData(nextFormData);

            toast.success(
                passwordChangeActive
                    ? 'Account details and password updated successfully.'
                    : 'Account details updated successfully.'
            );
        } catch (err) {
            console.error('Failed to update account:', err);
            toast.error(err instanceof Error ? err.message : 'Failed to update account details');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <MyAccountPageShell
                className="weka-my-account-page--account-details"
                title="Account Details"
                subtitle="Update your profile information and manage your security settings."
            >
                <div className="weka-account-details__sections">
                    <MyAccountCard
                        variant="page"
                        slug="account-details-personal-loading"
                        className="weka-account-details__skeleton-card"
                        wrapContent={false}
                    >
                        <div className="skeleton" style={{ height: '24px', width: '220px', marginBottom: '1rem' }} />
                        <div className="weka-account-details__grid">
                            <div className="skeleton" style={{ height: '72px' }} />
                            <div className="skeleton" style={{ height: '72px' }} />
                            <div className="skeleton" style={{ height: '92px', gridColumn: '1 / -1' }} />
                            <div className="skeleton" style={{ height: '72px', gridColumn: '1 / -1' }} />
                        </div>
                    </MyAccountCard>
                    <MyAccountCard
                        variant="page"
                        slug="account-details-security-loading"
                        className="weka-account-details__skeleton-card"
                        wrapContent={false}
                    >
                        <div className="skeleton" style={{ height: '24px', width: '150px', marginBottom: '1rem' }} />
                        <div className="weka-account-details__grid">
                            <div className="skeleton" style={{ height: '72px', gridColumn: '1 / -1' }} />
                            <div className="skeleton" style={{ height: '72px' }} />
                            <div className="skeleton" style={{ height: '72px' }} />
                        </div>
                    </MyAccountCard>
                    <div className="skeleton" style={{ height: '54px', width: '180px', marginLeft: 'auto' }} />
                </div>
            </MyAccountPageShell>
        );
    }

    return (
        <MyAccountPageShell
            className="weka-my-account-page--account-details"
            title="Account Details"
            subtitle="Update your profile information and manage your security settings."
        >
            {!canEditAccountDetails && (
                <div className="alert alert-info" role="status">
                    Editing account details is disabled for this page.
                </div>
            )}

            <form className="woocommerce-EditAccountForm edit-account" onSubmit={handleSubmit}>
                <fieldset className="weka-account-details__fieldset" disabled={!canEditAccountDetails || saving}>
                    <div className="weka-account-details__sections">
                        <MyAccountCard
                            as="section"
                            ariaLabelledBy="account-details-personal-title"
                            variant="page"
                            slug="account-details-personal"
                            className="weka-account-details__card"
                            wrapContent={false}
                        >
                            <div className="weka-account-details__card-header">
                                <div className="weka-account-details__card-heading">
                                    <span className="weka-account-details__card-accent" aria-hidden="true" />
                                    <div>
                                        <h3
                                            className="weka-account-details__card-title"
                                            id="account-details-personal-title"
                                        >
                                            Personal Information
                                        </h3>
                                        <p className="weka-account-details__card-description">
                                            Keep your profile details up to date for your account and reviews.
                                        </p>
                                    </div>
                                </div>

                                <span className="weka-account-details__card-icon" aria-hidden="true">
                                    <i className="fas fa-user"></i>
                                </span>
                            </div>

                            <div className="weka-account-details__grid">
                                <label className="weka-account-details__field" htmlFor="first_name">
                                    <span className="weka-account-details__label">
                                        First Name <span className="weka-account-details__required">*</span>
                                    </span>
                                    <input
                                        type="text"
                                        className="woocommerce-Input woocommerce-Input--text input-text weka-account-details__input"
                                        name="first_name"
                                        id="first_name"
                                        value={formData.first_name}
                                        onChange={handleChange}
                                        required
                                        autoComplete="given-name"
                                    />
                                </label>

                                <label className="weka-account-details__field" htmlFor="last_name">
                                    <span className="weka-account-details__label">
                                        Last Name <span className="weka-account-details__required">*</span>
                                    </span>
                                    <input
                                        type="text"
                                        className="woocommerce-Input woocommerce-Input--text input-text weka-account-details__input"
                                        name="last_name"
                                        id="last_name"
                                        value={formData.last_name}
                                        onChange={handleChange}
                                        required
                                        autoComplete="family-name"
                                    />
                                </label>

                                <label className="weka-account-details__field weka-account-details__field--full" htmlFor="display_name">
                                    <span className="weka-account-details__label">
                                        Display Name <span className="weka-account-details__required">*</span>
                                    </span>
                                    <input
                                        type="text"
                                        className="woocommerce-Input woocommerce-Input--text input-text weka-account-details__input"
                                        name="display_name"
                                        id="display_name"
                                        value={formData.display_name}
                                        onChange={handleChange}
                                        required
                                    />
                                    <span className="weka-account-details__hint">
                                        This is how your name will appear in the dashboard and reviews.
                                    </span>
                                </label>

                                <label className="weka-account-details__field weka-account-details__field--full" htmlFor="email">
                                    <span className="weka-account-details__label">
                                        Email Address <span className="weka-account-details__required">*</span>
                                    </span>
                                    <input
                                        type="email"
                                        className="woocommerce-Input woocommerce-Input--email input-text weka-account-details__input"
                                        name="email"
                                        id="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        autoComplete="email"
                                    />
                                </label>
                            </div>
                        </MyAccountCard>

                        <MyAccountCard
                            as="section"
                            ariaLabelledBy="account-details-security-title"
                            variant="page"
                            slug="account-details-security"
                            className="weka-account-details__card"
                            wrapContent={false}
                        >
                            <div className="weka-account-details__card-header">
                                <div className="weka-account-details__card-heading">
                                    <span className="weka-account-details__card-accent" aria-hidden="true" />
                                    <div>
                                        <h3
                                            className="weka-account-details__card-title"
                                            id="account-details-security-title"
                                        >
                                            Security
                                        </h3>
                                        <p className="weka-account-details__card-description">
                                            Update your password any time. Leave these fields blank to keep it unchanged.
                                        </p>
                                    </div>
                                </div>

                                <span className="weka-account-details__card-icon" aria-hidden="true">
                                    <i className="fas fa-lock"></i>
                                </span>
                            </div>

                            <div className="weka-account-details__grid">
                                <label className="weka-account-details__field weka-account-details__field--full" htmlFor="current_password">
                                    <span className="weka-account-details__label">Current Password</span>
                                    <input
                                        type="password"
                                        className="woocommerce-Input woocommerce-Input--password input-text weka-account-details__input"
                                        name="current_password"
                                        id="current_password"
                                        value={formData.current_password}
                                        onChange={handleChange}
                                        autoComplete="current-password"
                                        placeholder="Enter your current password"
                                    />
                                </label>

                                <label className="weka-account-details__field" htmlFor="new_password">
                                    <span className="weka-account-details__label">New Password</span>
                                    <input
                                        type="password"
                                        className="woocommerce-Input woocommerce-Input--password input-text weka-account-details__input"
                                        name="new_password"
                                        id="new_password"
                                        value={formData.new_password}
                                        onChange={handleChange}
                                        autoComplete="new-password"
                                        placeholder="New password"
                                    />
                                </label>

                                <label className="weka-account-details__field" htmlFor="confirm_password">
                                    <span className="weka-account-details__label">Confirm Password</span>
                                    <input
                                        type="password"
                                        className="woocommerce-Input woocommerce-Input--password input-text weka-account-details__input"
                                        name="confirm_password"
                                        id="confirm_password"
                                        value={formData.confirm_password}
                                        onChange={handleChange}
                                        autoComplete="new-password"
                                        placeholder="Confirm password"
                                    />
                                </label>
                            </div>

                            <p className="weka-account-details__section-note">
                                Password changes require your current password and a new password with at least 8 characters.
                            </p>
                        </MyAccountCard>
                    </div>

                    <div className="weka-account-details__footer">
                        <button
                            type="button"
                            className="weka-account-details__discard"
                            onClick={handleDiscard}
                            disabled={!isDirty || saving}
                        >
                            Discard changes
                        </button>

                        <button
                            type="submit"
                            className="btn btn-primary weka-account-details__save"
                            disabled={saving || !canEditAccountDetails || !isDirty}
                        >
                            <i className="fas fa-save" aria-hidden="true"></i>
                            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                        </button>
                    </div>
                </fieldset>
            </form>
        </MyAccountPageShell>
    );
};

export default MyAccountDetails;
